import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ActionBar from './ActionBar';
import { scrollToTop } from '../utils/scrollToTop';
import { standaloneNavigationState } from '../utils/qrEntry';
import {
  WithdrawalReceipt,
  WithdrawalSession,
  WithdrawalSituation,
} from '../utils/types';
import { useStandaloneQr } from '../contexts/StandaloneQrContext';
import { errorBodyOf, situationOf } from '../services/orderLookupService';
import {
  DeclareError,
  declareErrorOf,
  newIdempotencyKey,
  postWithdrawal,
} from '../services/withdrawalService';
import { currentCredentials } from '../services/portalCredentials';
import {
  clearDraft,
  doneReceiptId,
  loadDraft,
  markDone,
  saveContext,
  saveDraft,
  portalEntryUrl,
} from '../services/withdrawalContext';

// #149 (S3 of the withdrawal function, art. 11^1 OUG 34/2014): the
// declaration form. Name, the order (read-only), the acknowledgement e-mail,
// the scope (whole contract) and „Confirmați retragerea”. One idempotency key
// per order, persisted in this tab, reused on every retry and reload, so a
// lost answer recovers the same receipt and a click burst records one
// declaration.
// #154 (S3b): opened by the single entry's dispatcher (a WITHDRAWAL-routed
// lookup); the intro follows the lookup's situation preview (D4, legalDraft;
// presentation only, the receipt follows the classified case).

interface FormState {
  session: WithdrawalSession;
  situation: WithdrawalSituation;
  proof: string | null;
  shop: string | null;
}

// same e-mail check as the lookup (OrderFormPage)
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormError = Exclude<DeclareError, 'invalid' | 'notFound'>;

const WithdrawalFormPage: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'dynamic']);
  const navigate = useNavigate();
  const location = useLocation();
  const { active: standaloneQr, handoff, account } = useStandaloneQr();
  const state = location.state as Partial<FormState> | null;
  const session = state?.session ?? null;
  const proof = state?.proof ?? null;
  const shop = state?.shop ?? null;
  const situation = situationOf(state?.situation);
  const shopId = session?.order.shop_id ?? 0;
  const orderId = session?.order.id ?? 0;
  const [doneId] = useState(() =>
    session ? doneReceiptId(shopId, orderId) : null
  );

  const [name, setName] = useState(
    () =>
      (session && loadDraft(shopId, orderId)?.name) ??
      session?.prefill_name ??
      ''
  );
  const [email, setEmail] = useState(session?.prefill_email ?? '');
  const [fieldErrors, setFieldErrors] = useState<{
    name?: boolean;
    email?: boolean;
  }>({});
  const [error, setError] = useState<FormError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const announced = useRef(false);
  useEffect(() => {
    if (!titleRef.current || announced.current) return;
    announced.current = true;
    scrollToTop();
    titleRef.current.focus({ preventScroll: true });
  }, []);
  // review r1: the attempt key of this form while it is mounted (storage is
  // only the durable copy: a storage failure never changes the key)
  const keyRef = useRef<string | null>(
    session ? (loadDraft(shopId, orderId)?.key ?? null) : null
  );
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const flag = standaloneQr ? standaloneNavigationState(handoff, account) : {};

  useEffect(() => {
    if (!session) {
      // #154: back to the single entry of the same storefront
      navigate(portalEntryUrl(proof, shop), { replace: true });
    } else if (doneId !== null) {
      // the declaration of this order was recorded (a late answer after the
      // customer left): recover its receipt, never declare again
      navigate('/withdrawal/' + doneId, {
        replace: true,
        state: standaloneQr
          ? standaloneNavigationState(handoff, account)
          : undefined,
      });
    }
  }, [session, doneId, navigate, standaloneQr, handoff, proof, shop]);

  if (!session || doneId !== null) return null;

  const declare = async () => {
    if (inFlight.current || error === 'disabledByShop') return;
    // review r1: a declaration of this order completed meanwhile (a late
    // answer of a form the customer left): recover it, never post again
    const completed = doneReceiptId(shopId, orderId);
    if (completed !== null) {
      navigate('/withdrawal/' + completed, {
        replace: true,
        state: standaloneQr
          ? standaloneNavigationState(handoff, account)
          : undefined,
      });
      return;
    }
    const errors = {
      name: name.trim() === '',
      email: !EMAIL.test(email.trim()),
    };
    setFieldErrors(errors);
    if (errors.name || errors.email) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    // the key of this order's form: created on the first submit and kept;
    // the name of every attempted POST is stored before it is sent
    const key =
      keyRef.current ?? loadDraft(shopId, orderId)?.key ?? newIdempotencyKey();
    keyRef.current = key;
    saveDraft(shopId, orderId, { key, name });
    try {
      const credentials = currentCredentials(standaloneQr);
      if (credentials === null) {
        if (aliveRef.current) setError('sessionExpired');
        return;
      }
      const response = await postWithdrawal(
        {
          order_id: session.order.id,
          name: name.trim(),
          email: email.trim(),
          locale: i18n.language,
        },
        credentials.token,
        credentials.shopId,
        key
      );
      if (response.ok) {
        const receipt: WithdrawalReceipt = await response.json();
        // durable completion first, whatever happens to this page
        markDone(shopId, orderId, receipt.id);
        const context = {
          receipt,
          order: session.order,
          proof,
          shop,
          standalone: standaloneQr,
        };
        saveContext(context);
        clearDraft(shopId, orderId);
        if (aliveRef.current) {
          navigate('/withdrawal/' + receipt.id, {
            replace: true,
            state: { ...context, ...flag },
          });
        }
        return;
      }
      const body = await errorBodyOf(response);
      if (!aliveRef.current) return;
      const kind = declareErrorOf(response.status, body);
      if (kind === 'invalid') {
        const message = String(
          (body as { message?: unknown } | null)?.message ?? ''
        ).toLowerCase();
        setFieldErrors(
          message.includes('mail') ? { email: true } : { name: true }
        );
      } else {
        setError(kind === 'notFound' ? 'sessionExpired' : kind);
      }
    } catch {
      if (aliveRef.current) setError('generic');
    } finally {
      inFlight.current = false;
      if (aliveRef.current) setSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    declare();
  };

  // literal keys (orphan-key gate)
  const errorMessage = (kind: FormError): string => {
    switch (kind) {
      case 'sessionExpired':
        return t('withdrawal.sessionExpired');
      case 'conflict':
        return t('withdrawal.errorConflict');
      case 'disabledByShop':
        return t('withdrawal.disabledByShop');
      case 'portalInactive':
        return t('withdrawal.portalInactive');
      default:
        return t('withdrawal.errorGeneric');
    }
  };

  // the intro of the situation (literal keys, orphan-key gate)
  const intro = (): string => {
    switch (situation) {
      case 'NOT_SHIPPED':
        return t('withdrawal.introNotShipped');
      case 'DELIVERED':
        return t('withdrawal.caseCReturn');
      case 'SHIPPED':
        return t('withdrawal.introShipped');
      default:
        return t('withdrawal.introUnknown');
    }
  };

  const order = session.order;
  const displayIdentifier = order.order_name?.trim() || order.id;
  const entryUrl = portalEntryUrl(proof, shop);

  return (
    <>
      <div className="xc-screen xc-withdrawal-form">
        <div className="xc-withdrawal-header">
          <p className="xc-withdrawal-step">{t('withdrawal.stepIndicator')}</p>
          <div className="xc-title-row">
            <h2 ref={titleRef} tabIndex={-1} className="xc-title">
              {t('withdrawal.identifiedTitle', {
                order: displayIdentifier,
              })}
            </h2>
          </div>
        </div>
        <p className="xc-intro">{intro()}</p>
        <form
          className="xc-card xc-card--withdraw"
          onSubmit={handleSubmit}
          noValidate
        >
          <h3 className="xc-card__title">{t('withdrawal.formTitle')}</h3>
          <div className="xc-notice xc-notice--withdraw" role="note">
            <span>{t('withdrawal.destructiveWarning')}</span>
          </div>
          <dl className="xc-kv">
            <dt>{t('withdrawal.order')}</dt>
            <dd>{displayIdentifier}</dd>
            {Array.isArray(order.line_items) && (
              <>
                <dt>{t('withdrawal.products')}</dt>
                <dd>
                  {order.line_items.reduce(
                    (sum, item) => sum + item.quantity,
                    0
                  )}
                </dd>
              </>
            )}
            {order.total_price != null && order.currency && (
              <>
                <dt>{t('withdrawal.total')}</dt>
                <dd className="xc-withdrawal-total">
                  {new Intl.NumberFormat(i18n.language, {
                    style: 'currency',
                    currency: order.currency,
                  }).format(order.total_price)}
                </dd>
              </>
            )}
          </dl>
          <div className="xc-withdrawal-fields">
            <div className="xc-field">
              <label htmlFor="withdrawal-name" className="xc-field__label">
                {t('withdrawal.name')} <span className="xc-field__req">*</span>
              </label>
              <input
                type="text"
                className="xc-input"
                id="withdrawal-name"
                name="name"
                autoComplete="name"
                value={name}
                onChange={e => {
                  setName(e.target.value);
                  setFieldErrors(prev => ({ ...prev, name: false }));
                }}
                disabled={submitting || error === 'disabledByShop'}
                aria-invalid={fieldErrors.name || undefined}
                aria-describedby={
                  fieldErrors.name ? 'withdrawal-name-error' : undefined
                }
              />
              {fieldErrors.name && (
                <p
                  className="xc-field__error"
                  id="withdrawal-name-error"
                  role="alert"
                >
                  {t('withdrawal.nameRequired')}
                </p>
              )}
            </div>
            <div className="xc-field">
              <label htmlFor="withdrawal-email" className="xc-field__label">
                {t('withdrawal.email')} <span className="xc-field__req">*</span>
              </label>
              <input
                type="email"
                className="xc-input"
                id="withdrawal-email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  setFieldErrors(prev => ({ ...prev, email: false }));
                }}
                disabled={submitting || error === 'disabledByShop'}
                aria-invalid={fieldErrors.email || undefined}
                aria-describedby={
                  fieldErrors.email ? 'withdrawal-email-error' : undefined
                }
              />
              {fieldErrors.email && (
                <p
                  className="xc-field__error"
                  id="withdrawal-email-error"
                  role="alert"
                >
                  {t('withdrawal.emailInvalid')}
                </p>
              )}
            </div>
          </div>
          <div className="xc-withdrawal-submit">
            <p className="xc-field__help">{t('withdrawal.scope')}</p>
            <button
              type="submit"
              className="xc-btn xc-btn--withdraw xc-btn--block"
              disabled={submitting || error === 'disabledByShop'}
            >
              {submitting ? (
                <>
                  <span className="xc-spinner" aria-hidden="true"></span>
                  {t('buttons.submitting')}
                </>
              ) : (
                // Art. 11^1 requires an unambiguous confirmation of withdrawal.
                t('withdrawal.confirmButton')
              )}
            </button>
          </div>
          {error && (
            <div
              className={
                error === 'disabledByShop'
                  ? 'xc-notice xc-notice--withdraw'
                  : error === 'generic'
                    ? 'xc-notice xc-notice--error'
                    : 'xc-notice xc-notice--warn'
              }
              role="alert"
            >
              <p className="xc-notice__text">{errorMessage(error)}</p>
              {error === 'generic' && (
                <button
                  type="button"
                  className="xc-btn xc-btn--sm"
                  onClick={() => declare()}
                >
                  {t('buttons.retry')}
                </button>
              )}
              {error === 'sessionExpired' && !standaloneQr && (
                <Link to={entryUrl} className="xc-btn xc-btn--sm">
                  {t('orderDetails.goSearch')}
                </Link>
              )}
            </div>
          )}
        </form>
      </div>
      <ActionBar
        hideBack={standaloneQr}
        onBack={() => navigate(entryUrl)}
        backLabel={t('buttons.back')}
      />
    </>
  );
};

export default WithdrawalFormPage;
